import { PRODUCT_ALIAS_FIELDS } from "./productAliases";
import {
  PRODUCT_BLOCK_CLOSE_MARKER,
  productBlockOpenMarker,
} from "./productBlocks";
import {
  hasProductContextMarkers,
  PRODUCT_CONTEXT_CLOSE_MARKER,
  productContextOpenMarker,
} from "./productContexts";
import { productItemIdentifier } from "./productItemTags";
import type { ProductGridModelValue } from "@/features/EmailEditor/plugins/productGridConstants";

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Bare product-data aliases only — the exact (case-sensitive, filter-free)
 * shape the V1 transform resolved. `{{cart_url}}` / `{{support_link}}` and
 * filtered variants like `{{product_name|capfirst}}` never transformed in V1
 * and stay untouched here too.
 */
const ALIAS_PATTERN = new RegExp(
  `\\{\\{\\s*(?:${PRODUCT_ALIAS_FIELDS.map((a) => escapeRegExp(a.identifier)).join("|")})\\s*\\}\\}`,
);

const ALIAS_REWRITES = PRODUCT_ALIAS_FIELDS.map(
  ({ identifier, field }) =>
    [
      new RegExp(`\\{\\{\\s*${escapeRegExp(identifier)}\\s*\\}\\}`, "g"),
      // Chip-normalized form (no inner spaces) so the editor round-trips the
      // migrated text byte-identically — the dirty-check baseline depends on it.
      `{{${productItemIdentifier(0, field)}}}`,
    ] as const,
);

/**
 * Rewrites a legacy V1 body (bare `{{product_name}}`-style aliases, always the
 * zero-indexed item of the tile-derived model) into the V2 dialect: one
 * `{% with items=tile_data.<model> %}` context spanning the first through last
 * alias-bearing line, with the span wrapped in a `{% if items.0 %}` product
 * block and each alias rewritten to `{{items.0.<field>}}`.
 *
 * The block guard changes render behavior vs V1: a missing product now drops
 * the block instead of rendering empty values. That is the intended V2 shape —
 * the author lands with a real, guarded product block rather than loose chips.
 *
 * Returns the same reference when there is nothing to migrate — deterministic
 * and idempotent (the output contains no bare aliases).
 */
export function migrateLegacyProductAliases(
  bodyText: string,
  productModel: ProductGridModelValue,
): string {
  if (!ALIAS_PATTERN.test(bodyText)) return bodyText;
  // A body that already speaks the V2 dialect is not a V1 draft — don't nest
  // contexts around whatever else it contains.
  if (hasProductContextMarkers(bodyText)) return bodyText;

  const lines = bodyText.split("\n");
  const aliasBearing = lines.map((line) => ALIAS_PATTERN.test(line));
  const first = aliasBearing.indexOf(true);
  const last = aliasBearing.lastIndexOf(true);

  const rewrite = (line: string): string =>
    ALIAS_REWRITES.reduce(
      (acc, [pattern, replacement]) => acc.replace(pattern, replacement),
      line,
    );

  return [
    ...lines.slice(0, first),
    productContextOpenMarker(productModel),
    productBlockOpenMarker(0),
    ...lines.slice(first, last + 1).map(rewrite),
    PRODUCT_BLOCK_CLOSE_MARKER,
    PRODUCT_CONTEXT_CLOSE_MARKER,
    ...lines.slice(last + 1),
  ].join("\n");
}
